import * as THREE from 'three'
import type { ClashBox, ClashResult } from '@viewer/clash/lib'
import type {
  Viewpoint,
  VpClippingPlane,
  VpModelRef,
  VpVec3,
} from '@viewer/lib/viewpoint'
import { IDENTITY_ORIENTATION } from '@viewer/store/sectionSlice'
import type { Referencial } from '@modules/bcf/lib/bcfCoordenadas'
import type { BcfCatalogEntry, BcfExtensions } from '@modules/bcf/lib/bcfParser'
import { CLASH_COLOR_A, CLASH_COLOR_B, corBcf } from './clashCores'
import { chaveDosGuids } from './clashReconciliacao'
import {
  clashKey,
  clashLabel,
  ESPECIALIDADES,
  PRIORITIES,
  PRIORITY_BCF,
  STATUS_BCF,
  TOPIC_TYPE_CLASH,
  temDecisao,
  triageOf,
  type ClashPriority,
  type ClashTriageEntry,
  type ClashTriageMap,
  type ClashTriageState,
} from './clashTriage'

// ---------------------------------------------------------------------------
// Conflitos -> BCF.
//
// Um conflito da um topico. Um conflito que pertence a um grupo leva o
// grupo indicado: a etiqueta `Grupo <id>`, os RelatedTopics para os
// outros topicos do mesmo grupo na mesma exportacao e, na descricao, uma
// primeira linha `Grupo <id>: <titulo> (conflito i de n)` com a descricao
// do grupo a seguir. O modulo BCF agrupa a lista por essa etiqueta (ou
// pelos RelatedTopics, nos BCF de outras plataformas). A opcao antiga,
// um GRUPO de conflitos num so topico, continua disponivel quando se
// pede. O viewpoint e CONSTRUIDO a partir do `point` e
// da `bbox` do resultado (em coordenadas da cena, ver docs/CLASH.md) e a
// conversao para as coordenadas do IFC faz-se so na fronteira do .bcfv,
// no `bcfBuilder` (bcfCoordenadas.ts). O viewpoint leva:
//
//   - camara em perspectiva, numa direccao obliqua fixa (a mesma em todos
//     os topicos: percorrer um relatorio com a camara a rodar desorienta);
//   - os elementos do conflito em Selection;
//   - Coloring com A a ciano e B a magenta (as cores do realce);
//   - os 6 ClippingPlanes de uma caixa a volta da zona, com folga, para a
//     zona aparecer isolada noutras ferramentas (e a caixa de corte
//     equivalente, para o nosso viewer);
//   - Visibility com DefaultVisibility a true.
//
// As imagens (snapshots) sao opcionais e capturadas a parte
// (clashSnapshots.ts), com a mesma camara e a mesma caixa.
//
// A profundidade vai SEMPRE anotada como aproximada: e uma sondagem, nao
// uma medicao (docs/CLASH.md, seccao 3). A folga e exacta ao nivel dos
// triangulos e vai como valor.
// ---------------------------------------------------------------------------

/** Distancia minima da camara ao ponto, em metros. */
const MIN_CAM_DIST = 2.5
/** Folga da caixa de corte a volta da zona, em metros. */
export const FOLGA_CORTE_M = 1
/** Aresta minima da caixa de corte, em metros: uma interseccao de 2 mm
 *  dava uma caixa onde nao se via nada. */
export const ARESTA_MINIMA_CORTE_M = 2
/** Campo de visao vertical da camara dos topicos, em graus. */
export const FOV_TOPICO = 60

export interface ClashTopicDraft {
  /** Chave do topico: o `clashKey` do conflito, ou `grupo:<id>`. */
  key: string
  /** `clashKey` de todos os conflitos do topico. */
  keys: string[]
  /** O que da o Guid estavel do topico: `grupo:<id>` num grupo; num
   *  conflito, o par de GlobalId e o tipo (o mesmo entre revisoes do
   *  IFC, ao contrario do localId da chave), ou a chave sem GlobalIds. */
  identidade: string
  title: string
  description: string
  labels: string[]
  type: string
  status: string
  /** Priority do BCF (Critical, High, Normal, Low), ou null. */
  priority: string | null
  assignedTo: string | null
  /** Especialidades a quem comunicar (vao para as Labels e para a
   *  lista Users do extensions.xml). */
  notify: string[]
  /** O que as corridas fizeram aos conflitos do topico: um Comment por
   *  evento (agregados num grupo, por corrida e por tipo). */
  comentarios: { data: string; texto: string; autor: string | null }[]
  /** DueDate do BCF (data e hora ISO), ou null. */
  dueDate: string | null
  /** Grupo indicado no topico (o id da triagem), ou null. Num topico de
   *  conflito, os outros topicos com o mesmo grupo nesta exportacao vao
   *  nos RelatedTopics. */
  grupo: string | null
}

/** Milímetros com vírgula decimal (PT-PT). Na lista e nos tópicos BCF,
 *  que seguem para outras equipas. */
export function formatMm(metres: number | undefined): string {
  if (metres === undefined || !Number.isFinite(metres)) return '?'
  const mm = metres * 1000
  if (mm >= 100) return `${Math.round(mm)} mm`
  if (mm >= 10) return `${decimal(mm, 1)} mm`
  return `${decimal(mm, 2)} mm`
}

/** `toFixed` com vírgula decimal. Determinístico (não depende da
 *  localização do browser, ao contrário do `toLocaleString`). */
function decimal(n: number, casas: number): string {
  return n.toFixed(casas).replace('.', ',')
}

/** GlobalIds resolvidos para um conflito, na ordem A, B. */
export type ClashGuidMap = Record<string, [string | null, string | null]>

// --- Itens a exportar --------------------------------------------------------

export interface ClashExportItem {
  /** `clashKey` do conflito, ou `grupo:<id>` num topico de grupo. */
  key: string
  results: ClashResult[]
  /** Identificador do grupo, quando o topico junta um grupo. */
  group: string | null
  /** Grupo a que o conflito pertence, quando o topico e de UM conflito
   *  (indicado na etiqueta, nos RelatedTopics e na descricao). Num topico
   *  de grupo e o proprio grupo. */
  grupoIndicado?: string | null
}

/**
 * Os topicos a criar. Sem `umPorGrupo` (a omissao), um por conflito, com
 * o grupo indicado. Com ele, os conflitos do mesmo grupo (da proposta ou
 * do agrupamento automatico) juntam-se num topico; os sem grupo
 * continuam um a um. A ordem e a dos resultados.
 */
export function itensParaExportar(
  results: readonly ClashResult[],
  triage: ClashTriageMap = {},
  umPorGrupo = false,
): ClashExportItem[] {
  const itens: ClashExportItem[] = []
  const porGrupo = new Map<string, ClashExportItem>()
  for (const r of results) {
    const key = clashKey(r)
    const doConflito = triage[key]?.group || null
    const grupo = umPorGrupo ? doConflito : null
    if (!grupo) {
      itens.push({ key, results: [r], group: null, grupoIndicado: doConflito })
      continue
    }
    const existente = porGrupo.get(grupo)
    if (existente) {
      existente.results.push(r)
      continue
    }
    const item: ClashExportItem = { key: `grupo:${grupo}`, results: [r], group: grupo, grupoIndicado: grupo }
    porGrupo.set(grupo, item)
    itens.push(item)
  }
  return itens
}

/**
 * Os conflitos que a exportacao leva. Marcados, se houver marcados.
 * Senao, os por resolver; com `soComTriagem`, so os que tem triagem
 * (estado decidido, prioridade, responsavel, grupo da proposta...):
 * corre-se com folga para apanhar as folgas da proposta sem levar para o
 * BCF as centenas de folgas que a proposta nao tem.
 *
 * `ignorados`: os conflitos ignorados por regra de exclusao
 * (clashExclusao.ts). Ficam de fora, mesmo marcados; quem os quer no BCF
 * pede-o ("Incluir os ignorados por regra") e nao os passa aqui.
 */
export function conflitosAExportar(
  results: readonly ClashResult[],
  triage: ClashTriageMap,
  opts: { checked?: ReadonlySet<string>; soComTriagem?: boolean; ignorados?: ReadonlySet<string> | ReadonlyMap<string, unknown> } = {},
): ClashResult[] {
  const ignorados = opts.ignorados
  const lista = ignorados && ignorados.size > 0 ? results.filter((r) => !ignorados.has(clashKey(r))) : results
  if (opts.checked && opts.checked.size > 0) {
    return lista.filter((r) => opts.checked!.has(clashKey(r)))
  }
  return lista.filter((r) => {
    const e = triageOf(triage, clashKey(r))
    if (e.state === 'resolvido') return false
    return !opts.soComTriagem || temDecisao(e)
  })
}

/** Ha conflitos com grupo, ou seja, faz sentido indicar o grupo e
 *  oferecer "um topico por grupo"? */
export function temGrupos(
  results: readonly ClashResult[],
  triage: ClashTriageMap,
): boolean {
  return results.some((r) => (triage[clashKey(r)]?.group ?? '') !== '')
}

// --- Triagem de um topico (um conflito ou um grupo) ---------------------------

interface TriagemDoTopico {
  state: ClashTriageState
  priority: ClashPriority | null
  assignedTo: string | null
  notify: string[]
  recommendations: string[]
  dueDate: string | null
  tags: string[]
  notes: string[]
  groupTitle: string | null
}

function unicos(lista: readonly string[]): string[] {
  const out: string[] = []
  for (const v of lista) {
    const t = v.trim()
    if (t && !out.includes(t)) out.push(t)
  }
  return out
}

/** Sem acentos, minusculas, sem espacos nas pontas: para comparar
 *  etiquetas sem diferenciar maiusculas nem acentos. */
function chaveDeTexto(v: string): string {
  return v
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

/** Unicos sem diferenciar maiusculas nem acentos (fica a 1.a grafia). */
function unicosCi(lista: readonly string[]): string[] {
  const vistos = new Set<string>()
  const out: string[] = []
  for (const v of lista) {
    const t = v.trim()
    const k = chaveDeTexto(t)
    if (!t || vistos.has(k)) continue
    vistos.add(k)
    out.push(t)
  }
  return out
}

/** "Piso 1", "P1" e "Nível P1" designam o mesmo: tira as palavras de
 *  piso e nivel, os espacos e o "P" antes do numero. */
function designacaoDePiso(v: string): string {
  return chaveDeTexto(v)
    .replace(/\b(piso|nivel|level|storey|andar)\b/g, '')
    .replace(/\s+/g, '')
    .replace(/^p(?=\d)/, '')
}

const SIGLAS = ESPECIALIDADES.map((e) => e.sigla)
const RE_SIGLA = new RegExp(`\\b(${SIGLAS.join('|')})\\b`, 'g')

/** Palavras do tipo de conflito que a proposta tambem poe nas
 *  etiquetas e que o tipo do topico ja diz. */
const PALAVRAS_DE_TIPO = [
  'atravessamento',
  'folga',
  'interferencia',
  'sobreposicao',
  'embebido',
  'duplicacao',
  'duplicacao/sobreposicao',
  'entrega/apoio',
  'conflito',
]

/**
 * As etiquetas (Labels) de um topico: "Conflito", o tipo, as
 * especialidades (as siglas ARQ, EST, MAC, HID e EEL que aparecem nas
 * etiquetas, no "a comunicar" e no responsavel), o nivel e o piso
 * declarado, este so quando difere do nivel; depois as etiquetas livres
 * que nao repetem nada disto. Sem repetidos, sem diferenciar maiusculas
 * nem acentos. O texto do responsavel nao entra (vai no AssignedTo).
 */
export function etiquetasDoTopico(o: {
  tipos: readonly string[]
  assignedTo: string | null
  notify: readonly string[]
  tags: readonly string[]
  pisos: readonly string[]
}): string[] {
  const siglas: string[] = []
  for (const texto of [...o.tags, ...o.notify, o.assignedTo ?? '']) {
    for (const m of texto.matchAll(RE_SIGLA)) siglas.push(m[1])
  }
  const niveis = o.tags.filter((t) => /^n[ií]vel\b/i.test(t.trim()))
  const designacoesDeNivel = new Set(niveis.map(designacaoDePiso))
  const pisosDeclarados = [
    ...o.tags.filter((t) => /^piso\b/i.test(t.trim())),
    ...o.pisos,
  ].filter((p) => !designacoesDeNivel.has(designacaoDePiso(p)))
  const pisosUnicos: string[] = []
  const vistosPiso = new Set<string>()
  // Com o nível por cota, os pisos declarados ficam de fora: são a
  // associação de cada elemento a um IfcBuildingStorey, que nos modelos
  // reais nem sempre bate com a cota (no Cha139, condutas do P5 no
  // "Piso 3" e vigas da cobertura no "Piso 1"), e enchiam o tópico de
  // etiquetas que se contradizem. Sem nível, o piso declarado é o que há.
  for (const p of niveis.length > 0 ? [] : pisosDeclarados) {
    const d = designacaoDePiso(p)
    if (vistosPiso.has(d)) continue
    vistosPiso.add(d)
    pisosUnicos.push(p)
  }
  const tiposCi = new Set([...o.tipos.map(chaveDeTexto), ...PALAVRAS_DE_TIPO])
  const responsavel = chaveDeTexto(o.assignedTo ?? '')
  const livres = o.tags.filter((t) => {
    const k = chaveDeTexto(t)
    if (!k || tiposCi.has(k) || k === responsavel) return false
    if (SIGLAS.some((s) => chaveDeTexto(s) === k)) return false
    if (/^(piso|n[ií]vel)\b/i.test(t.trim())) return false
    if (designacoesDeNivel.has(designacaoDePiso(t))) return false
    return true
  })
  return unicosCi([
    'Conflito',
    ...o.tipos,
    ...siglas,
    ...niveis,
    ...pisosUnicos,
    ...livres,
  ])
}

/** Junta a triagem dos conflitos de um topico. Num grupo: o estado
 *  menos avancado (um conflito ainda novo deixa o topico aberto), a
 *  prioridade mais alta, o prazo mais cedo, as listas unidas. */
function triagemDoTopico(
  entries: readonly ClashTriageEntry[],
): TriagemDoTopico {
  // Do menos para o mais avancado: um par reaberto deixa o topico
  // aberto, tal como um novo.
  const ordemEstado: ClashTriageState[] = ['reaberto', 'novo', 'aceite', 'resolvido']
  let state: ClashTriageState = 'resolvido'
  let priority: ClashPriority | null = null
  let dueDate: string | null = null
  let assignedTo: string | null = null
  let groupTitle: string | null = null
  const notify: string[] = []
  const recs: string[] = []
  const tags: string[] = []
  const notes: string[] = []
  for (const e of entries) {
    if (ordemEstado.indexOf(e.state) < ordemEstado.indexOf(state)) state = e.state
    if (e.priority && (priority === null || PRIORITIES.indexOf(e.priority) < PRIORITIES.indexOf(priority))) {
      priority = e.priority
    }
    if (e.dueDate && (dueDate === null || e.dueDate < dueDate)) dueDate = e.dueDate
    if (!assignedTo && e.assignedTo) assignedTo = e.assignedTo
    if (!groupTitle && e.groupTitle) groupTitle = e.groupTitle
    notify.push(...(e.notify ?? []))
    tags.push(...(e.tags ?? []))
    if (e.recommendation) recs.push(e.recommendation)
    if (e.note.trim()) notes.push(e.note.trim())
  }
  if (entries.length === 0) state = 'novo'
  return {
    state,
    priority,
    assignedTo,
    notify: unicos(notify),
    recommendations: unicos(recs),
    dueDate,
    tags: unicos(tags),
    notes: unicos(notes),
    groupTitle,
  }
}

// --- Rascunhos de topico -----------------------------------------------------

export interface ClashDraftOptions {
  guids?: ClashGuidMap
  triage?: ClashTriageMap
  /** Pisos de cada conflito, por `clashKey` (quando se souberem). */
  pisos?: Record<string, string[]>
  /** Referencial do modelo, para escrever o ponto em coordenadas IFC.
   *  Sem ele, so a troca de eixos (modelo sem georreferenciacao). */
  referencial?: Referencial | null
  /** Grupos do agrupamento automatico: a descricao (metodo, regras)
   *  entra na descricao do topico do grupo, ou de cada conflito do
   *  grupo; o titulo serve quando a triagem nao o tem. */
  grupos?: Record<string, { descricao?: string; titulo?: string }>
  /** Nomes (Name do IFC) dos dois elementos de cada conflito, por
   *  `clashKey`, na ordem A, B. Servem o titulo do topico. */
  nomes?: Record<string, [string | null, string | null]>
}

function tipoDoConflito(r: ClashResult): string {
  return r.kind === 'hard' ? 'Atravessamento' : 'Folga'
}

// A descricao e curta e escrita para quem resolve (15/10/2026, pedido do
// Rui: os textos eram longos de mais e liam-se como gerados): o que
// acontece e onde, quem resolve, a quem comunicar e o que fazer. O que a
// maquina usa (GlobalId, ponto, camara) vai no viewpoint, nao no texto.

/** O que fazer, tal como vem da classificacao. Quem resolve, a quem
 *  comunicar, o prazo, o estado, o grupo e a regra ja estao nos campos do
 *  topico (AssignedTo, Labels, DueDate, Status) e nao se repetem aqui
 *  (16/10/2026, pedido do Rui: "nao precisa estar na descricao o grupo"). */
function linhasDoQueFazer(t: TriagemDoTopico): string[] {
  if (t.recommendations.length === 1) return [t.recommendations[0]]
  return t.recommendations.map((r) => `- ${r}`)
}

/** Milímetros para ler: inteiros a partir de 10 mm, uma casa abaixo. */
function mmParaLer(m: number | null | undefined): string {
  if (m === null || m === undefined || !Number.isFinite(m)) return '?'
  const mm = m * 1000
  return mm >= 10 ? `${Math.round(mm)} mm` : `${decimal(mm, 1)} mm`
}

/** "Atravessamento de cerca de 70 mm" / "Folga de 12 mm". */
function oQueAcontece(r: ClashResult): string {
  return r.kind === 'hard'
    ? `Atravessamento de cerca de ${mmParaLer(r.depth)}`
    : `Folga de ${mmParaLer(r.distance)}`
}

/** Os eventos das corridas como comentarios. Num grupo, os eventos da
 *  mesma corrida e do mesmo tipo juntam-se num comentario. */
function comentariosDosEventos(
  entries: readonly ClashTriageEntry[],
): ClashTopicDraft['comentarios'] {
  const juntos = new Map<string, { data: string; texto: string; autor: string | null; n: number }>()
  for (const e of entries) {
    for (const ev of e.eventos ?? []) {
      const k = `${ev.data}|${ev.tipo}`
      const j = juntos.get(k)
      if (j) j.n += 1
      else juntos.set(k, { data: ev.data, texto: ev.texto, autor: ev.autor ?? null, n: 1 })
    }
  }
  return [...juntos.values()]
    .map((j) => ({
      data: j.data,
      autor: j.autor,
      texto: j.n === 1 ? j.texto : `${j.n} pares do grupo. ${j.texto}`,
    }))
    .sort((a, b) => a.data.localeCompare(b.data))
}

/** Comprimento maximo do identificador de um elemento no titulo. */
export const MAX_NOME_NO_TITULO = 40

/** Primeira letra em maiuscula. */
function capitalizar(t: string): string {
  return t.length > 0 ? t[0].toUpperCase() + t.slice(1) : t
}

/**
 * Identificador curto de um elemento para o titulo: o Name do IFC, sem o
 * id de elemento que o Revit acrescenta no fim (":123456") e com os
 * espacos juntos, cortado a `MAX_NOME_NO_TITULO` caracteres; sem nome, a
 * classe IFC.
 */
export function elementoNoTitulo(
  nome: string | null | undefined,
  categoria: string | null | undefined,
): string {
  const limpo = (nome ?? '').replace(/:\d+$/, '').replace(/\s+/g, ' ').trim()
  if (!limpo) return categoria ?? 'elemento'
  return limpo.length > MAX_NOME_NO_TITULO
    ? `${limpo.slice(0, MAX_NOME_NO_TITULO - 1).trimEnd()}…`
    : limpo
}

/**
 * Titulo do topico de UM conflito, pensado para uma lista:
 *
 *   <Problema>: <elemento A> x <elemento B>[, <nivel>] (<medida>)
 *
 * O problema e o tipo da classificacao automatica (Atravessamento,
 * Duplicacao, Entrega/apoio...), ou, sem classificacao, Atravessamento
 * num conflito duro e Folga numa folga. Os elementos sao o nome (ou a
 * classe) de cada um. O nivel e o nivel por cota da classificacao, ou o
 * piso quando os dois elementos estao no mesmo. A medida e a
 * profundidade aproximada ou a folga livre. O titulo do grupo nao entra:
 * vai na descricao e na etiqueta. Deterministico: os mesmos dados dao o
 * mesmo titulo.
 */
export function tituloDoConflito(
  r: ClashResult,
  e: Pick<ClashTriageEntry, 'tipoConflito' | 'nivel'> | undefined,
  nomes: readonly [string | null, string | null] | undefined,
  pisos: readonly string[] = [],
): string {
  const tipo = e?.tipoConflito?.trim()
  const problema = tipo ? capitalizar(tipo) : tipoDoConflito(r)
  const a = elementoNoTitulo(nomes?.[0], r.categoryA)
  const b = elementoNoTitulo(nomes?.[1], r.categoryB)
  const nivel = e?.nivel?.trim() || (pisos.length === 1 ? pisos[0] : '')
  const medida = r.kind === 'hard' ? formatMm(r.depth) : `${formatMm(r.distance)} livres`
  return `${problema}: ${a} x ${b}${nivel ? `, ${nivel}` : ''} (${medida})`
}

/** Prefixo das etiquetas de grupo (o modulo BCF agrupa por elas). */
export const PREFIXO_ETIQUETA_GRUPO = 'Grupo '

/** Etiqueta do grupo num topico: `Grupo <id>`, com o id tal como esta na
 *  triagem (automatico "G-" + hash, ou o da proposta). */
export function etiquetaDoGrupo(id: string): string {
  return `${PREFIXO_ETIQUETA_GRUPO}${id}`
}

function identidadeDoItem(item: ClashExportItem, opts: ClashDraftOptions): string {
  if (item.group || item.results.length !== 1) return item.key
  const r = item.results[0]
  const k = clashKey(r)
  const [a, b] = opts.guids?.[k] ?? opts.triage?.[k]?.guids ?? [null, null]
  return a && b ? `guids:${chaveDosGuids([a, b], r.kind)}` : k
}

/** Posicao de um conflito no seu grupo, nesta exportacao. */
interface PosicaoNoGrupo {
  i: number
  n: number
}

function draftDoItem(
  item: ClashExportItem,
  opts: ClashDraftOptions,
  _posicao: PosicaoNoGrupo | null,
): ClashTopicDraft {
  const keys = item.results.map(clashKey)
  const entries = keys.map((k) => triageOf(opts.triage ?? {}, k))
  const t = triagemDoTopico(entries)
  const pisos = unicos(keys.flatMap((k) => opts.pisos?.[k] ?? []))
  const tipos = unicos(item.results.map(tipoDoConflito))
  const linhas: string[] = []
  const grupo = item.group ?? item.grupoIndicado ?? null

  let title: string
  if (item.results.length === 1) {
    const r = item.results[0]
    title = tituloDoConflito(r, opts.triage?.[keys[0]], opts.nomes?.[keys[0]], opts.pisos?.[keys[0]] ?? [])
    // O titulo ja diz o problema, os elementos, o nivel e a medida; a
    // descricao diz o que fazer. Sem recomendacao, o que acontece.
    const fazer = linhasDoQueFazer(t)
    if (fazer.length > 0) linhas.push(...fazer)
    else linhas.push(`${oQueAcontece(r)}${pisos.length > 0 ? `, ${pisos[0]}` : ''}.`)
  } else {
    const n = item.results.length
    const nome = t.groupTitle ?? clashLabel(item.results[0])
    // O titulo da proposta ja diz o que o grupo e (e muitas vezes ja traz
    // a contagem): usa-se tal como vem, com o identificador a frente.
    // Os grupos automaticos (id "G-" + hash) saem so com o titulo: o id
    // e uma identidade, nao um nome. Os da proposta levam o id a frente.
    title = t.groupTitle
      ? item.group?.startsWith('G-')
        ? t.groupTitle
        : `${item.group} · ${t.groupTitle}`
      : `Grupo ${item.group}: ${nome} (${n} conflitos)`
    linhas.push(`${n} conflitos${pisos.length > 0 ? `, ${pisos.join(', ')}` : ''}.`)
    linhas.push(...linhasDoQueFazer(t))
    item.results.forEach((r, i) => {
      const nomes = opts.nomes?.[keys[i]] ?? [null, null]
      const a = nomes[0] ?? r.categoryA ?? 'sem categoria'
      const b = nomes[1] ?? r.categoryB ?? 'sem categoria'
      const pisosDoPar = opts.pisos?.[keys[i]] ?? []
      linhas.push(
        `${i + 1}. ${a} x ${b}: ${oQueAcontece(r).toLowerCase()}${pisosDoPar.length > 0 ? `, ${pisosDoPar[0]}` : ''}.`,
      )
    })
  }
  // As marcas internas da plataforma ("por verificar", profundidade
  // estimada) nao saem no BCF: liam-se como texto gerado (Rui, 16/10/2026).
  for (const nota of t.notes) linhas.push(`Nota: ${nota}`)

  const labels = etiquetasDoTopico({
    tipos,
    assignedTo: t.assignedTo,
    notify: t.notify,
    tags: t.tags,
    pisos,
  })
  // A etiqueta do grupo vai no fim, tal e qual (sem passar pela limpeza
  // das etiquetas livres): e por ela que o modulo BCF agrupa.
  if (grupo && !labels.includes(etiquetaDoGrupo(grupo))) labels.push(etiquetaDoGrupo(grupo))

  return {
    key: item.key,
    identidade: identidadeDoItem(item, opts),
    keys,
    title,
    description: linhas.join('\n'),
    labels,
    type: TOPIC_TYPE_CLASH,
    status: STATUS_BCF[t.state],
    priority: t.priority ? PRIORITY_BCF[t.priority] : null,
    assignedTo: t.assignedTo,
    notify: t.notify,
    comentarios: comentariosDosEventos(entries),
    // Meia-noite UTC: em Portugal continental e nas ilhas a data lida e
    // a mesma que foi escrita.
    dueDate: t.dueDate ? `${t.dueDate}T00:00:00Z` : null,
    grupo,
  }
}

/** Rascunhos de topico para os itens dados. Puro: quem resolve os
 *  GlobalIds, os nomes e os pisos, e quem escreve o BCF, e o chamador.
 *
 *  Os titulos dos topicos de conflito sao unicos na exportacao: dois
 *  conflitos com os mesmos nomes, nivel e medida levam no fim os
 *  localId dos dois elementos (" · #12 x #34"). */
export function buildItemDrafts(
  itens: readonly ClashExportItem[],
  opts: ClashDraftOptions = {},
): ClashTopicDraft[] {
  // Conflito i de n: a posicao no grupo pela ordem da exportacao.
  const membros = new Map<string, number>()
  const posicoes: (PosicaoNoGrupo | null)[] = itens.map((item) => {
    const g = item.group ? null : (item.grupoIndicado ?? null)
    if (!g) return null
    const i = (membros.get(g) ?? 0) + 1
    membros.set(g, i)
    return { i, n: 0 }
  })
  itens.forEach((item, k) => {
    const p = posicoes[k]
    if (p && item.grupoIndicado) p.n = membros.get(item.grupoIndicado) ?? p.i
  })
  const drafts = itens.map((item, k) => draftDoItem(item, opts, posicoes[k]))
  const vezes = new Map<string, number>()
  for (const d of drafts) vezes.set(d.title, (vezes.get(d.title) ?? 0) + 1)
  itens.forEach((item, k) => {
    if (item.group || item.results.length !== 1) return
    if ((vezes.get(drafts[k].title) ?? 0) < 2) return
    const r = item.results[0]
    drafts[k].title = `${drafts[k].title} · #${r.pair.localIdA} x #${r.pair.localIdB}`
  })
  return drafts
}

/** Os topicos relacionados de cada rascunho: num topico de conflito com
 *  grupo, os Guids dos outros topicos de conflito do mesmo grupo nesta
 *  exportacao (pela ordem da exportacao). `guids` e o Guid de cada
 *  rascunho, pela mesma ordem. */
export function relacionadosDosRascunhos(
  drafts: readonly ClashTopicDraft[],
  guids: readonly string[],
): string[][] {
  const deConflito = (d: ClashTopicDraft) => !!d.grupo && !d.key.startsWith('grupo:')
  const porGrupo = new Map<string, string[]>()
  drafts.forEach((d, i) => {
    if (!deConflito(d)) return
    const l = porGrupo.get(d.grupo!)
    if (l) l.push(guids[i])
    else porGrupo.set(d.grupo!, [guids[i]])
  })
  return drafts.map((d, i) =>
    deConflito(d) ? (porGrupo.get(d.grupo!) ?? []).filter((g) => g !== guids[i]) : [],
  )
}

/** Um topico por conflito (atalho para os chamadores antigos). */
export function buildClashTopicDrafts(
  results: readonly ClashResult[],
  opts: ClashDraftOptions = {},
): ClashTopicDraft[] {
  return buildItemDrafts(itensParaExportar(results), opts)
}

// --- Extensoes ----------------------------------------------------------------

/** Valores para a lista Users do extensions.xml: as especialidades
 *  responsaveis e as a comunicar, sem repetidos. */
export function utilizadoresDosTopicos(drafts: readonly ClashTopicDraft[]): string[] {
  return unicos(drafts.flatMap((d) => [...(d.assignedTo ? [d.assignedTo] : []), ...d.notify]))
}

/**
 * Catalogo do extensions.xml para os topicos de conflitos: o tipo Clash,
 * os tres estados e as quatro prioridades (todos, para se poderem mudar
 * noutra ferramenta) e as etiquetas que os topicos usam. Ids
 * deterministicos: o mesmo conjunto de topicos da o mesmo ficheiro.
 */
export function extensoesDosConflitos(drafts: readonly ClashTopicDraft[]): BcfExtensions {
  const entrada = (grupo: string, label: string, i: number): BcfCatalogEntry => ({
    id: `conflitos-${grupo}-${i}`,
    label,
    color: null,
    sortOrder: i,
    source: 'default',
  })
  const estados: [string, boolean][] = [
    [STATUS_BCF.novo, false],
    [STATUS_BCF.aceite, false],
    [STATUS_BCF.resolvido, true],
  ]
  return {
    types: [entrada('tipo', TOPIC_TYPE_CLASH, 0)],
    statuses: estados.map(([label, isTerminal], i) => ({ ...entrada('estado', label, i), isTerminal })),
    priorities: PRIORITIES.map((p, i) => entrada('prioridade', PRIORITY_BCF[p], i)),
    labels: unicos(drafts.flatMap((d) => d.labels)).map((l, i) => entrada('etiqueta', l, i)),
    stages: [],
  }
}

// --- Viewpoint ----------------------------------------------------------------

/** Caixa (cena) a volta das zonas dos conflitos, com folga e aresta
 *  minima. E a caixa de corte do viewpoint e da imagem. */
/** Meia aresta maxima da zona de UM conflito a volta do seu ponto, em
 *  metros. A `bbox` da interferencia pode ser enorme (uma viga embebida
 *  ao comprido numa laje, uma contencao que da a sobreposicao das
 *  caixas): a zona fica sempre perto do ponto do conflito. */
export const MEIA_ZONA_MAXIMA_M = 0.75

/** Zona de um conflito: a caixa da interferencia, recortada a
 *  `MEIA_ZONA_MAXIMA_M` a volta do ponto. Nunca a caixa dos elementos. */
export function zonaDoConflito(r: ClashResult): THREE.Box3 {
  const p = new THREE.Vector3(...r.point)
  const lim = new THREE.Box3(
    p.clone().subScalar(MEIA_ZONA_MAXIMA_M),
    p.clone().addScalar(MEIA_ZONA_MAXIMA_M),
  )
  const zona = new THREE.Box3(
    new THREE.Vector3(...r.bbox.min),
    new THREE.Vector3(...r.bbox.max),
  )
  const ok = [...r.bbox.min, ...r.bbox.max].every((v) => Number.isFinite(v))
  if (!ok || zona.isEmpty()) return new THREE.Box3(p.clone(), p.clone())
  zona.intersect(lim)
  if (zona.isEmpty()) return new THREE.Box3(p.clone(), p.clone())
  return zona.expandByPoint(p)
}

export function caixaDeCorte(
  results: readonly ClashResult[],
  folga = FOLGA_CORTE_M,
  arestaMinima = ARESTA_MINIMA_CORTE_M,
): ClashBox {
  const box = new THREE.Box3()
  for (const r of results) box.union(zonaDoConflito(r))
  if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3())
  box.expandByScalar(folga)
  const size = box.getSize(new THREE.Vector3())
  const centro = box.getCenter(new THREE.Vector3())
  box.setFromCenterAndSize(
    centro,
    new THREE.Vector3(
      Math.max(size.x, arestaMinima),
      Math.max(size.y, arestaMinima),
      Math.max(size.z, arestaMinima),
    ),
  )
  return {
    min: [box.min.x, box.min.y, box.min.z],
    max: [box.max.x, box.max.y, box.max.z],
  }
}

/** Os 6 planos da caixa, com a normal para FORA (o lado cortado), a
 *  mesma convencao da caixa de corte exportada pelo viewer
 *  (`bcfPlanesFromSection`). */
export function planosDaCaixa(c: ClashBox): VpClippingPlane[] {
  const [x0, y0, z0] = c.min
  const [x1, y1, z1] = c.max
  const cx = (x0 + x1) / 2
  const cy = (y0 + y1) / 2
  const cz = (z0 + z1) / 2
  const p = (location: VpVec3, direction: VpVec3): VpClippingPlane => ({ location, direction })
  return [
    p({ x: x0, y: cy, z: cz }, { x: -1, y: 0, z: 0 }),
    p({ x: x1, y: cy, z: cz }, { x: 1, y: 0, z: 0 }),
    p({ x: cx, y: y0, z: cz }, { x: 0, y: -1, z: 0 }),
    p({ x: cx, y: y1, z: cz }, { x: 0, y: 1, z: 0 }),
    p({ x: cx, y: cy, z: z0 }, { x: 0, y: 0, z: -1 }),
    p({ x: cx, y: cy, z: z1 }, { x: 0, y: 0, z: 1 }),
  ]
}

export interface ClashViewpointOptions {
  /** GlobalIds na ordem A, B (um conflito). */
  guids?: (string | null)[]
  /** GlobalIds do lado A e do lado B (topico de grupo). Tem precedencia
   *  sobre `guids`. */
  guidsA?: (string | null)[]
  guidsB?: (string | null)[]
  modelRefs?: VpModelRef[]
  /** Proporcao largura/altura da imagem, quando ha imagem. */
  aspect?: number
  /** Sem caixa de corte (so a camara e os componentes). */
  semCorte?: boolean
}

function soGuids(lista: readonly (string | null | undefined)[] | undefined): string[] {
  return unicos((lista ?? []).filter((g): g is string => typeof g === 'string' && g.length > 0))
}

/**
 * Viewpoint BCF apontado a um conflito, ou a um grupo de conflitos.
 * Camara em perspectiva, numa direccao obliqua fixa, afastada o
 * suficiente para a zona caber no ecra. Coordenadas da cena: o builder
 * converte-as para IFC ao escrever o .bcfv.
 */
export function clashViewpoint(
  r: ClashResult | readonly ClashResult[],
  opts: ClashViewpointOptions = {},
): Viewpoint {
  const results: readonly ClashResult[] = Array.isArray(r) ? r : [r as ClashResult]
  // A camara parte das zonas dos conflitos (pontos de interferencia),
  // nao das caixas dos elementos: uma laje inteira afastava-a do edificio.
  const zona = new THREE.Box3()
  for (const x of results) zona.union(zonaDoConflito(x))
  const point =
    results.length === 1
      ? new THREE.Vector3(...results[0].point)
      : zona.getCenter(new THREE.Vector3())
  // Distancia para a caixa de corte (a zona com folga) caber inteira no
  // campo de visao vertical: o raio da esfera que a envolve sobre o seno
  // de meio campo. E a mesma caixa que corta a imagem e as outras
  // ferramentas, por isso o que se ve e o que fica.
  const corte = caixaDeCorte(results)
  const raio =
    Math.hypot(
      corte.max[0] - corte.min[0],
      corte.max[1] - corte.min[1],
      corte.max[2] - corte.min[2],
    ) / 2
  const dist = Math.max(MIN_CAM_DIST, raio / Math.sin(((FOV_TOPICO / 2) * Math.PI) / 180))
  // Y e o eixo vertical do viewer (three). A direccao escolhida olha de
  // cima e de lado, que e como se le um no estrutural.
  const offset = new THREE.Vector3(1, 0.8, 1).normalize().multiplyScalar(dist)
  const position = point.clone().add(offset)
  const direction = point.clone().sub(position).normalize()

  const a = opts.guidsA ? soGuids(opts.guidsA) : soGuids([opts.guids?.[0]])
  const b = opts.guidsB ? soGuids(opts.guidsB) : soGuids([opts.guids?.[1]])
  // Sem A/B conhecidos, o que vier em `guids` vai seleccionado na mesma.
  const selected = unicos([...a, ...b, ...(opts.guidsA ? [] : soGuids(opts.guids))])
  const coloring = [
    ...(a.length > 0 ? [{ color: corBcf(CLASH_COLOR_A), global_ids: a }] : []),
    ...(b.length > 0 ? [{ color: corBcf(CLASH_COLOR_B), global_ids: b }] : []),
  ]

  const caixa = opts.semCorte ? null : caixaDeCorte(results)
  return {
    camera: {
      mode: 'perspective',
      position: { x: position.x, y: position.y, z: position.z },
      direction: { x: direction.x, y: direction.y, z: direction.z },
      up: { x: 0, y: 1, z: 0 },
      target: { x: point.x, y: point.y, z: point.z },
      field_of_view: FOV_TOPICO,
      ...(opts.aspect && Number.isFinite(opts.aspect) ? { aspect_ratio: opts.aspect } : {}),
    },
    clipping_planes: caixa ? planosDaCaixa(caixa) : [],
    components: {
      visible_global_ids: [],
      hidden_global_ids: [],
      selected_global_ids: selected,
      default_visibility: true,
      ...(coloring.length > 0 ? { coloring } : {}),
      // A mesma caixa como caixa de corte do nosso viewer: abrir o
      // topico no VN2R repoe a zona isolada (os ClippingPlanes servem as
      // outras ferramentas).
      ...(caixa
        ? {
            section: {
              enabled: true,
              orientation: { ...IDENTITY_ORIENTATION, origin: [0, 0, 0] as [number, number, number] },
              extents: {
                minX: caixa.min[0],
                maxX: caixa.max[0],
                minY: caixa.min[1],
                maxY: caixa.max[1],
                minZ: caixa.min[2],
                maxZ: caixa.max[2],
              },
            },
          }
        : {}),
    },
    model_refs: opts.modelRefs ?? [],
  }
}

/**
 * Ancora do pin BCF de um topico de conflitos, no mundo three (o mesmo
 * referencial do `anchor` dos viewpoints capturados, viewpointCapture.ts):
 * o ponto da interferencia num conflito; num topico de grupo, o centro
 * (media) dos pontos dos conflitos. Null sem pontos finitos.
 */
export function ancoraDosConflitos(results: readonly ClashResult[]): [number, number, number] | null {
  const pontos = results.map((r) => r.point).filter((p) => p.every((v) => Number.isFinite(v)))
  if (pontos.length === 0) return null
  const soma = pontos.reduce((a, p) => [a[0] + p[0], a[1] + p[1], a[2] + p[2]], [0, 0, 0])
  return [soma[0] / pontos.length, soma[1] / pontos.length, soma[2] / pontos.length]
}

/** Nome de ficheiro seguro para a exportacao. */
export function clashBcfFileName(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `conflitos_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.bcfzip`
}
